// =============================================================================
// Module Teams - Zustand Store
// =============================================================================

import { create } from 'zustand';
import type { Team, TeamMemberWithProfile } from '../types';
import {
  fetchTeams,
  fetchAllTeamMembers,
  createTeam,
  updateTeam,
  deleteTeam,
  addTeamMember,
  removeTeamMember,
  updateTeamMemberRole,
} from '../data/teamRepository';

interface TeamsState {
  teams: Team[];
  teamMembers: TeamMemberWithProfile[]; // all team members across all teams
  loading: boolean;
  error: string | null;

  loadTeams: (orgId: string) => Promise<void>;
  createTeam: (orgId: string, name: string, description?: string, color?: string) => Promise<Team | null>;
  updateTeam: (teamId: string, updates: Partial<Pick<Team, 'name' | 'description' | 'color' | 'avatar_url'>>) => Promise<boolean>;
  deleteTeam: (teamId: string) => Promise<boolean>;
  addMember: (teamId: string, orgMemberId: string, role?: 'lead' | 'member') => Promise<boolean>;
  removeMember: (teamId: string, orgMemberId: string) => Promise<boolean>;
  updateMemberRole: (teamId: string, orgMemberId: string, role: 'lead' | 'member') => Promise<boolean>;
  getMembersForTeam: (teamId: string) => TeamMemberWithProfile[];
}

export const useTeamsStore = create<TeamsState>((set, get) => ({
  teams: [],
  teamMembers: [],
  loading: false,
  error: null,

  loadTeams: async (orgId: string) => {
    set({ loading: true, error: null });
    try {
      const [teams, members] = await Promise.all([
        fetchTeams(orgId),
        fetchAllTeamMembers(orgId),
      ]);
      set({ teams, teamMembers: members, loading: false });
    } catch (err) {
      set({ error: 'Erreur lors du chargement des équipes', loading: false });
    }
  },

  createTeam: async (orgId, name, description, color) => {
    const team = await createTeam(orgId, name, description, color);
    if (team) {
      set(s => ({ teams: [...s.teams, team] }));
    }
    return team;
  },

  updateTeam: async (teamId, updates) => {
    const ok = await updateTeam(teamId, updates);
    if (ok) {
      set(s => ({
        teams: s.teams.map(t => t.id === teamId ? { ...t, ...updates } : t),
      }));
    }
    return ok;
  },

  deleteTeam: async (teamId) => {
    const ok = await deleteTeam(teamId);
    if (ok) {
      set(s => ({
        teams: s.teams.filter(t => t.id !== teamId),
        teamMembers: s.teamMembers.filter(m => m.team_id !== teamId),
      }));
    }
    return ok;
  },

  addMember: async (teamId, orgMemberId, role = 'member') => {
    const ok = await addTeamMember(teamId, orgMemberId, role);
    if (ok) {
      // Reload to get profile info
      const state = get();
      const team = state.teams.find(t => t.id === teamId);
      if (team) {
        const members = await fetchAllTeamMembers(team.org_id);
        set({ teamMembers: members });
      }
    }
    return ok;
  },

  removeMember: async (teamId, orgMemberId) => {
    const ok = await removeTeamMember(teamId, orgMemberId);
    if (ok) {
      set(s => ({
        teamMembers: s.teamMembers.filter(
          m => !(m.team_id === teamId && m.org_member_id === orgMemberId)
        ),
      }));
    }
    return ok;
  },

  updateMemberRole: async (teamId, orgMemberId, role) => {
    const ok = await updateTeamMemberRole(teamId, orgMemberId, role);
    if (ok) {
      set(s => ({
        teamMembers: s.teamMembers.map(m =>
          m.team_id === teamId && m.org_member_id === orgMemberId
            ? { ...m, role }
            : m
        ),
      }));
    }
    return ok;
  },

  getMembersForTeam: (teamId: string) => {
    return get().teamMembers.filter(m => m.team_id === teamId);
  },
}));
